import { Component, OnDestroy } from '@angular/core';
import { SettingsService } from '../services/indexed-db/object-stores/settings/settings.service';
import { MatCard, MatCardContent } from '@angular/material/card';
import { TranslatePipe } from '@ngx-translate/core';
import {
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatError, MatInput } from '@angular/material/input';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import {
  defaultBikeParkingTimeInMinutes,
  defaultCarParkingTimeInMinutes,
  defaultMaximumTourLengthInHours,
  defaultTimePerLeadInMinutes,
  RouteSettingsService,
} from './route-settings.service';
import { Subscription } from 'rxjs';
import { FooterService } from '../reusable-components/footer/footer.service';
import { Router } from '@angular/router';

@Component({
  selector: 'app-route-settings',
  imports: [
    MatCard,
    MatCardContent,
    TranslatePipe,
    MatLabel,
    MatFormField,
    FormsModule,
    MatInput,
    ReactiveFormsModule,
    MatError,
  ],
  templateUrl: './route-settings.component.html',
  styleUrl: './route-settings.component.scss',
})
export class RouteSettingsComponent implements OnDestroy {
  carParkingTimeFormControlSubscription: Subscription | undefined;
  carParkingTimeFormControl = this.getNumberFormControl(
    0,
    30,
    defaultCarParkingTimeInMinutes,
  );
  bikeParkingTimeFormControlSubscription: Subscription | undefined;
  bikeParkingTimeFormControl = this.getNumberFormControl(
    0,
    20,
    defaultBikeParkingTimeInMinutes,
  );
  maximumTourLengthFromControlSubscription: Subscription | undefined;
  maximumTourLengthFormControl = this.getNumberFormControl(
    1,
    10,
    defaultMaximumTourLengthInHours,
  );
  timePerLeadFormControlSubscription: Subscription | undefined;
  timePerLeadFormControl = this.getNumberFormControl(
    1,
    30,
    defaultTimePerLeadInMinutes,
  );

  constructor(
    private readonly settingsService: SettingsService,
    private readonly routeSettingsService: RouteSettingsService,
    footerService: FooterService,
    router: Router,
  ) {
    this.fetchRouteSettings();
    this.setFormControlListeners();

    footerService.setLeftButton({
      label: 'components.route-settings.navigate-to-lead-pool-button',
      click: async () => {
        await router.navigateByUrl('lead-pool');
      },
    });
    footerService.setRightButton({
      label: 'components.route-settings.navigate-to-selected-leads-button',
      click: async () => {
        await router.navigateByUrl('lead-task');
      },
    });
  }

  setFormControlListeners() {
    this.carParkingTimeFormControlSubscription =
      this.carParkingTimeFormControl.valueChanges.subscribe((newValue) => {
        if (this.carParkingTimeFormControl.valid) {
          this.routeSettingsService.setCarParkingTimeInMinutes(
            Number.parseInt(newValue!),
          );
        }
      });
    this.bikeParkingTimeFormControlSubscription =
      this.bikeParkingTimeFormControl.valueChanges.subscribe((newValue) => {
        if (this.bikeParkingTimeFormControl.valid) {
          this.routeSettingsService.setBikeParkingTimeInMinutes(
            Number.parseInt(newValue!),
          );
        }
      });
    this.timePerLeadFormControlSubscription =
      this.timePerLeadFormControl.valueChanges.subscribe((newValue) => {
        if (this.timePerLeadFormControl.valid) {
          this.routeSettingsService.setTimePerLeadInMinutes(
            Number.parseInt(newValue!),
          );
        }
      });
    this.maximumTourLengthFromControlSubscription =
      this.maximumTourLengthFormControl.valueChanges.subscribe((newValue) => {
        if (this.maximumTourLengthFormControl.valid) {
          this.routeSettingsService.setMaximumTourLengthInHours(
            Number.parseInt(newValue!),
          );
        }
      });
  }

  fetchRouteSettings() {
    this.timePerLeadFormControl.setValue(
      this.routeSettingsService.getTimePerLeadInMinutes().toString(),
    );
    this.maximumTourLengthFormControl.setValue(
      this.routeSettingsService.getMaximumTourLengthInHours().toString(),
    );
    this.carParkingTimeFormControl.setValue(
      this.routeSettingsService.getCarParkingTimeInMinutes().toString(),
    );
    this.bikeParkingTimeFormControl.setValue(
      this.routeSettingsService.getBikeParkingTimeInMinutes().toString(),
    );
  }

  private getNumberFormControl(min: number, max: number, initialValue: number) {
    return new FormControl(initialValue.toString(), [
      Validators.required,
      Validators.min(min),
      Validators.max(max),
      Validators.pattern(/^[0-9]*$/),
    ]);
  }

  ngOnDestroy(): void {
    this.carParkingTimeFormControlSubscription?.unsubscribe();
    this.bikeParkingTimeFormControlSubscription?.unsubscribe();
    this.maximumTourLengthFromControlSubscription?.unsubscribe();
    this.timePerLeadFormControlSubscription?.unsubscribe();
  }
}
